/**
 * Esquema Zod del formulario de Órdenes (alta + edición).
 *
 * Valida la entrada del formulario ANTES de llamar a la API (`docs/
 * frontend_conventions.md`, Validación). El tipo del formulario se **infiere** del
 * esquema (`z.infer`), no se escribe a mano. Los campos reflejan el contrato de
 * `POST /api/v1/orders` (`progress/explore_orders_api.md`, §2.3): obligatorios
 * (número, cliente, atendido, monto, fechas, hora) y opcionales (estado de pago,
 * abono condicional, descripción, tipo de orden).
 *
 * El formulario trabaja en **formato de UI**: las fechas como `YYYY-MM-DD` (lo que
 * emite `<input type="date">`) y los montos como texto; la capa `api.ts` convierte
 * a lo que el backend espera (fechas `DD/MM/YYYY`). Aquí solo se valida que el
 * monto sea numérico positivo y que el abono cumpla `0 < abono < total` cuando el
 * estado de pago es `abonado` (misma regla que el service).
 */
import { z } from "zod";

import type { PaymentStatus } from "./types";

/** Estados de pago seleccionables (los 5 del SSR; ver `constants.PAYMENT_LABEL`). */
export const PAYMENT_STATUS_VALUES = [
  "pendiente_de_pago",
  "pagada_para_despacho",
  "abonado",
  "pagado",
  "orden_a_credito",
] as const satisfies readonly PaymentStatus[];

/** Tipos de orden seleccionables (solo aplica donde la sede permite láser). */
export const ORDER_TYPE_VALUES = ["normal", "laser"] as const;

/** Parsea un texto de monto (admite coma o punto decimal) a número, o `NaN`. */
function parseAmount(value: string): number {
  const normalized = value.trim().replace(",", ".");
  if (normalized === "") {
    return Number.NaN;
  }
  return Number(normalized);
}

/** Campo de monto en texto: obligatorio, numérico y positivo. */
const amountField = z
  .string()
  .trim()
  .min(1, "El monto es obligatorio.")
  .refine((value) => !Number.isNaN(parseAmount(value)), "El monto debe ser un número válido.")
  .refine((value) => parseAmount(value) > 0, "El monto debe ser positivo.");

/**
 * Esquema base del formulario de orden.
 *
 * `amount_paid` es texto opcional aquí; su obligatoriedad y rango (`0 < abono <
 * total`) se validan en el refinamiento de objeto, porque dependen de
 * `payment_status` y de `sale_amount` (validación cruzada).
 */
const orderFormBase = z.object({
  order_number: z
    .string()
    .trim()
    .min(1, "El número de orden es obligatorio.")
    .regex(/^\d+$/, "El número de orden debe ser numérico."),
  customer_name: z.string().trim().min(1, "El nombre del cliente es obligatorio."),
  attendant: z.string().trim().min(1, "El atendido es obligatorio."),
  sale_amount: amountField,
  invoice_date: z.string().trim().min(1, "La fecha de ingreso es obligatoria."),
  delivery_date: z.string().trim().min(1, "La fecha de entrega es obligatoria."),
  delivery_time: z
    .string()
    .trim()
    .regex(/^\d{2}:\d{2}$/, "La hora de entrega debe tener el formato HH:MM."),
  payment_status: z.enum(PAYMENT_STATUS_VALUES),
  amount_paid: z.string().trim().optional().default(""),
  description: z.string().trim().optional().default(""),
  order_type: z.enum(ORDER_TYPE_VALUES).optional().default("normal"),
});

/**
 * Esquema completo: base + regla del abono.
 *
 * Solo cuando `payment_status === "abonado"` el abono es obligatorio y debe ser
 * `0 < abono < monto total` (espejo de `OrderService`: `abono_invalid` /
 * `abono_too_high`). El error se ancla al campo `amount_paid`.
 */
export const orderFormSchema = orderFormBase.superRefine((values, ctx) => {
  if (values.payment_status !== "abonado") {
    return;
  }
  const paid = parseAmount(values.amount_paid ?? "");
  if (Number.isNaN(paid) || paid <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["amount_paid"],
      message: "El monto abonado debe ser mayor a 0.",
    });
    return;
  }
  const total = parseAmount(values.sale_amount);
  if (!Number.isNaN(total) && paid >= total) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["amount_paid"],
      message: "El monto abonado no puede ser mayor o igual al total de la orden.",
    });
  }
});

/** Valores del formulario de orden (inferidos del esquema). */
export type OrderFormValues = z.infer<typeof orderFormSchema>;

/** Lista de claves del formulario, tipada (para iterar campos editables). */
export const ORDER_FORM_FIELDS = [
  "order_number",
  "customer_name",
  "attendant",
  "sale_amount",
  "invoice_date",
  "delivery_date",
  "delivery_time",
  "payment_status",
  "amount_paid",
  "description",
  "order_type",
] as const satisfies readonly (keyof OrderFormValues)[];
